import { queryKeys } from '../queryKeys';

export type WorkspaceKey =
  | 'jobs_identifier'
  | 'jobs_prioritiser'
  | 'platform_prioritiser'
  | 'tension_identifier'
  | 'recipe_card_builder'
  | 'product_idea_generator'
  | 'product_idea_validator'
  | 'recipe_card_validator';

export interface MarketplaceProduct {
  id: number;
  name: string;
  short_description: string;
  long_description?: string;
  credits: number;
  duration_in_days: number;
  service_type: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  product_category: number;
  product_category_workspace_key?: string;
  is_free?: boolean;
  display_order?: number;
}

export type MarketplaceProductPricingInput = Pick<
  MarketplaceProduct,
  'credits' | 'duration_in_days'
> & {
  service_type?: MarketplaceProduct['service_type'];
  is_free?: boolean;
};

export function formatMarketplaceProductPricing(product: MarketplaceProductPricingInput): string {
  if (product.service_type === 'DIY') return 'Included';
  if (product.is_free) return 'FREE';
  return `From ${product.credits} credits · ${product.duration_in_days} days`;
}

async function readJsonList<T>(response: Response): Promise<T[]> {
  const data = await response.json();
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}

export async function fetchMarketplaceProductsByWorkspaceKey(
  workspaceKey: WorkspaceKey
): Promise<MarketplaceProduct[]> {
  try {
    const response = await fetch(
      `/api/products/?workspace_key=${encodeURIComponent(workspaceKey)}`,
      { credentials: 'include' }
    );
    if (!response.ok) return [];
    return readJsonList<MarketplaceProduct>(response);
  } catch {
    return [];
  }
}

export function marketplaceProductsQuery(workspaceKey: WorkspaceKey) {
  return {
    queryKey: queryKeys.marketplace.products(workspaceKey),
    queryFn: () => fetchMarketplaceProductsByWorkspaceKey(workspaceKey),
    staleTime: 10 * 60 * 1000,
  };
}

export interface MarketplaceCatalog {
  types: unknown[];
  categories: unknown[];
  products: unknown[];
}

export async function fetchMarketplaceCatalog(): Promise<MarketplaceCatalog> {
  const [typesResponse, categoriesResponse, productsResponse] = await Promise.all([
    fetch('/api/category-types/', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }),
    fetch('/api/product-categories/', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }),
    fetch('/api/products/', {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }),
  ]);

  if (!typesResponse.ok || !categoriesResponse.ok || !productsResponse.ok) {
    throw new Error('Failed to load marketplace');
  }

  const [types, categories, products] = await Promise.all([
    typesResponse.json(),
    categoriesResponse.json(),
    productsResponse.json(),
  ]);

  return { types, categories, products };
}

export const AI_PIPELINE_WORKSPACE_KEYS: WorkspaceKey[] = [
  'jobs_identifier',
  'tension_identifier',
  'recipe_card_builder',
];

export interface PurchaseProduct {
  id: number;
  name: string;
  short_description: string;
  credits: number;
  duration_in_days: number;
  service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
  workspace_key?: string;
}

export function toPurchaseProduct(
  product: {
    id: number;
    name: string;
    short_description: string;
    credits: number;
    duration_in_days: number;
    service_type?: 'DIY' | 'AI' | 'AI Off-Platform' | 'Catalyx';
    product_category_workspace_key?: string;
    workspace_key?: string;
  },
  options?: { workspaceKey?: string }
): PurchaseProduct {
  return {
    id: product.id,
    name: product.name,
    short_description: product.short_description,
    credits: product.credits,
    duration_in_days: product.duration_in_days,
    service_type: product.service_type,
    workspace_key:
      product.product_category_workspace_key ??
      product.workspace_key ??
      options?.workspaceKey ??
      '',
  };
}

export function isAiPipelinePurchaseProduct(product: PurchaseProduct | null): boolean {
  if (!product || product.service_type !== 'AI') return false;
  return AI_PIPELINE_WORKSPACE_KEYS.includes(product.workspace_key as WorkspaceKey);
}

export const workspaceDiyStartClass =
  'shrink-0 px-6 py-2.5 rounded-full bg-[#43C8F5] text-white text-sm font-semibold hover:bg-[#2A9CC4] transition-colors whitespace-nowrap disabled:opacity-50';

export const workspaceExploreClass =
  'shrink-0 px-6 py-2.5 rounded-full bg-[#655FA8] text-white text-sm font-semibold hover:bg-[#544fa0] transition-colors whitespace-nowrap disabled:opacity-50';
